<script setup lang="ts">
// Catalog search with live suggestions (fetch + debounce live in
// composables/useProductSearch.ts, shared with the command palette):
// cover, platform and price under the field; arrow keys move, Enter opens
// the highlighted product or runs the full search, Escape closes. `/`
// focuses the field from anywhere (AppTopbar); the ⌘K hint opens the
// command palette.
import { onBeforeUnmount, ref, watch } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ArrowRight, Search } from 'lucide-vue-next';
import PriceTag from './PriceTag.vue';
import placeholderArt from '../assets/placeholder-card.jpg';
import { productCover } from '../utils/product-art';
import { SEARCH_MIN_CHARS, useProductSearch, useRecentSearches, type ProductSuggestion } from '../composables/useProductSearch';
import { PALETTE_MOD_KEY } from '../composables/usePaletteSections';
import { useUiStore } from '../stores/ui';

withDefaults(defineProps<{ showKbd?: boolean }>(), { showKbd: false });

const { t } = useI18n();
const router = useRouter();
const ui = useUiStore();
const { remember } = useRecentSearches();

const input = ref<HTMLInputElement | null>(null);
const root = ref<HTMLElement | null>(null);
const open = ref(false);
const highlighted = ref(-1);
const { query, trimmed, results, loading } = useProductSearch(() => (open.value = true));


watch(trimmed, (q) => {
  highlighted.value = -1;
  if (q.length < SEARCH_MIN_CHARS) open.value = false;
});

function goToCatalog() {
  const q = trimmed.value;
  close();
  remember(q);
  router.push({ name: 'catalog', query: q ? { q } : {} });
}

function submit() {
  const pick = results.value[highlighted.value];
  if (pick) {
    close();
    router.push(`/catalog/${pick.id}`);
    return;
  }
  goToCatalog();
}

function onKeydown(e: KeyboardEvent) {
  if (!open.value || results.value.length === 0) return;
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    highlighted.value = (highlighted.value + 1) % results.value.length;
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    highlighted.value = (highlighted.value - 1 + results.value.length) % results.value.length;
  } else if (e.key === 'Escape') {
    close();
  }
}

function close() {
  open.value = false;
  highlighted.value = -1;
}

function onOutside(e: MouseEvent) {
  if (root.value && !root.value.contains(e.target as Node)) close();
}
document.addEventListener('click', onOutside);
onBeforeUnmount(() => document.removeEventListener('click', onOutside));

function focus() {
  input.value?.focus();
}
defineExpose({ focus });

function coverOf(s: ProductSuggestion) {
  return productCover(s) ?? placeholderArt;
}
</script>

<template>
  <div ref="root" class="relative w-full">
    <form role="search" @submit.prevent="submit">
      <Search class="pointer-events-none absolute left-4 top-1/2 size-[18px] -translate-y-1/2 text-text-mute" :stroke-width="2" />
      <input
        ref="input"
        v-model="query"
        type="search"
        autocomplete="off"
        :aria-label="t('common.search')"
        :aria-expanded="open"
        aria-controls="search-suggestions"
        role="combobox"
        :placeholder="t('nav.searchPlaceholder')"
        class="h-11 w-full rounded-lg border border-white/10 bg-bg/70 pl-11 text-[14px] text-text shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] transition-[box-shadow,border-color] placeholder:text-text-mute focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/20 focus:shadow-glow"
        :class="showKbd ? 'pr-4 lg:pr-16' : 'pr-4'"
        @focus="results.length && (open = true)"
        @keydown="onKeydown"
      />
      <button
        v-if="showKbd"
        type="button"
        :aria-label="t('search.palette.open')"
        :title="t('search.palette.open')"
        class="absolute right-2.5 top-1/2 hidden h-6 -translate-y-1/2 items-center rounded-md border border-border bg-card px-1.5 font-mono text-[11px] text-text-mute transition-colors hover:border-text-mute hover:text-text lg:flex"
        @click="ui.openPalette()"
      >{{ PALETTE_MOD_KEY }} K</button>
    </form>

    <div
      v-if="open"
      id="search-suggestions"
      role="listbox"
      class="absolute inset-x-0 top-[calc(100%+8px)] z-50 overflow-hidden rounded-lg border border-border bg-surface shadow-lift"
    >
      <template v-if="results.length">
        <RouterLink
          v-for="(s, i) in results"
          :key="s.id"
          :to="`/catalog/${s.id}`"
          role="option"
          :aria-selected="highlighted === i"
          class="flex items-center gap-3 px-3 py-2 transition-colors"
          :class="highlighted === i ? 'bg-card' : 'hover:bg-card'"
          @mouseenter="highlighted = i"
          @click="close"
        >
          <img :src="coverOf(s)" alt="" class="h-10 w-[68px] shrink-0 rounded-md object-cover" loading="lazy" />
          <span class="min-w-0 flex-1">
            <span class="block truncate text-[14px] font-medium text-text">{{ s.name }}</span>
            <span v-if="s.platform && s.platform !== 'Other'" class="block text-[12px] text-text-mute">{{ s.platform }}</span>
          </span>
          <PriceTag :cents="s.ourPriceCents" size="sm" />
        </RouterLink>
        <button
          type="button"
          class="flex h-11 w-full items-center justify-between border-t border-border-dim px-4 font-display text-[13px] font-semibold text-primary transition-colors hover:bg-card"
          @click="goToCatalog"
        >
          {{ t('search.seeAll', { query: trimmed }) }}
          <ArrowRight class="size-4" :stroke-width="2.25" />
        </button>
      </template>
      <p v-else-if="!loading" class="px-4 py-4 text-[13px] text-text-dim">{{ t('search.noResults', { query: trimmed }) }}</p>
    </div>
  </div>
</template>
